import {
  GOOGLE_DRIVE_PICKER,
  MAX_MEDIA_FILES,
  MEDIA_UPLOAD_ALL_MIME_TYPES,
  MEDIA_UPLOAD_IMAGE_ONLY_MIME_TYPES
} from '../../../config/constants';
import { fetchBrowserFileFromUrl } from '../cloudPickerFileUtils';
import type {
  CloudPickerAdapter,
  CloudPickerFile,
  LaunchCloudPickerOptions
} from './types';

interface GoogleDrivePickerConfig {
  apiKey: string;
  clientId: string;
  appId: string;
}

type GoogleDrivePickerErrorCode =
  | 'auth_failed'
  | 'auth_cancelled'
  | 'config_missing'
  | 'download_failed';

export class GoogleDrivePickerError extends Error {
  code: GoogleDrivePickerErrorCode;

  constructor(code: GoogleDrivePickerErrorCode, message: string) {
    super(message);
    this.name = 'GoogleDrivePickerError';
    this.code = code;
  }
}

export const isGoogleDrivePickerError = (
  error: unknown
): error is GoogleDrivePickerError => error instanceof GoogleDrivePickerError;

interface GoogleTokenResponse {
  access_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

interface GoogleIdentityServicesError {
  type?: string;
  message?: string;
}

interface GoogleTokenClient {
  callback?: (response: GoogleTokenResponse) => void;
  error_callback?: (error: GoogleIdentityServicesError) => void;
  requestAccessToken: (options?: { prompt?: string }) => void;
}

interface GoogleDrivePickerDocument {
  id?: string;
  name?: string;
  mimeType?: string;
  sizeBytes?: number | string;
  [key: string]: unknown;
}

interface GoogleDrivePickerResponse {
  action?: string;
  docs?: GoogleDrivePickerDocument[];
  [key: string]: unknown;
}

declare global {
  interface Window {
    gapi?: {
      load: (
        apiName: string,
        callbackOrConfig:
          | (() => void)
          | {
              callback?: () => void;
              onerror?: () => void;
              ontimeout?: () => void;
              timeout?: number;
            }
      ) => void;
    };
    google?: {
      accounts?: {
        oauth2?: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback?: (response: GoogleTokenResponse) => void;
            error_callback?: (error: GoogleIdentityServicesError) => void;
          }) => GoogleTokenClient;
        };
      };
      picker?: any;
    };
  }
}

const GOOGLE_DRIVE_API_BASE_URL = 'https://www.googleapis.com/drive/v3/files';

const getGooglePickerApi = () =>
  typeof window === 'undefined' ? undefined : window.google?.picker;

let googleApiScriptPromise: Promise<void> | undefined;
let googleIdentityScriptPromise: Promise<void> | undefined;
let googlePickerLoadPromise: Promise<void> | undefined;
let cachedTokenClient:
  | {
      clientId: string;
      tokenClient: GoogleTokenClient;
    }
  | undefined;
let cachedAccessToken: string | null = null;
let cachedAccessTokenExpiresAt: number | null = null;

const ACCESS_TOKEN_REFRESH_SAFETY_SECONDS = 30;

const hasValidCachedAccessToken = () =>
  Boolean(
    cachedAccessToken &&
      cachedAccessTokenExpiresAt &&
      Date.now() < cachedAccessTokenExpiresAt
  );

const loadScript = (
  scriptId: string,
  src: string,
  isLoaded: () => boolean
) => {
  if (isLoaded()) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(
      scriptId
    ) as HTMLScriptElement | null;

    if (existingScript) {
      if (existingScript.dataset.loaded === 'true' || isLoaded()) {
        resolve();
        return;
      }

      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error(`Failed to load ${src}`)),
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.id = scriptId;
    script.src = src;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(script);
  });
};

const loadGoogleApiScript = async () => {
  if (!googleApiScriptPromise) {
    googleApiScriptPromise = loadScript(
      GOOGLE_DRIVE_PICKER.apiScriptId,
      GOOGLE_DRIVE_PICKER.apiSdkSrc,
      () => Boolean(window.gapi)
    );
  }

  return await googleApiScriptPromise;
};

const loadGoogleIdentityScript = async () => {
  if (!googleIdentityScriptPromise) {
    googleIdentityScriptPromise = loadScript(
      GOOGLE_DRIVE_PICKER.identityScriptId,
      GOOGLE_DRIVE_PICKER.identitySdkSrc,
      () => Boolean(window.google?.accounts?.oauth2)
    );
  }

  return await googleIdentityScriptPromise;
};

const loadGooglePickerApi = async () => {
  await loadGoogleApiScript();

  if (window.google?.picker) {
    return;
  }

  if (!googlePickerLoadPromise) {
    googlePickerLoadPromise = new Promise<void>((resolve, reject) => {
      if (!window.gapi) {
        reject(new Error('Google API loader unavailable'));
        return;
      }

      window.gapi.load('picker', {
        callback: () => resolve(),
        onerror: () => reject(new Error('Failed to load Google Picker API')),
        ontimeout: () => reject(new Error('Timed out loading Google Picker API')),
        timeout: 10000
      });
    });
  }

  return await googlePickerLoadPromise;
};

const validateGoogleDriveConfig = ({
  apiKey,
  clientId,
  appId
}: GoogleDrivePickerConfig) => {
  if (!apiKey || !clientId || !appId) {
    throw new GoogleDrivePickerError(
      'config_missing',
      'Missing Google Drive picker configuration.'
    );
  }
};

export const getAllowedGoogleDriveMimeTypes = (allowVideoAndPdf: boolean) =>
  allowVideoAndPdf
    ? MEDIA_UPLOAD_ALL_MIME_TYPES
    : MEDIA_UPLOAD_IMAGE_ONLY_MIME_TYPES;

export const buildGoogleDriveDownloadUrl = (fileId: string) => {
  const params = new URLSearchParams({
    alt: 'media',
    supportsAllDrives: 'true'
  });

  return `${GOOGLE_DRIVE_API_BASE_URL}/${encodeURIComponent(
    fileId
  )}?${params.toString()}`;
};

const getTokenClient = async ({ clientId }: GoogleDrivePickerConfig) => {
  await loadGoogleIdentityScript();

  const oauth = window.google?.accounts?.oauth2;
  if (!oauth) {
    throw new GoogleDrivePickerError(
      'auth_failed',
      'Google Identity Services is unavailable.'
    );
  }

  if (!cachedTokenClient || cachedTokenClient.clientId !== clientId) {
    cachedTokenClient = {
      clientId,
      tokenClient: oauth.initTokenClient({
        client_id: clientId,
        scope: GOOGLE_DRIVE_PICKER.scope,
        callback: () => {}
      })
    };
    cachedAccessToken = null;
    cachedAccessTokenExpiresAt = null;
  }

  return cachedTokenClient.tokenClient;
};

export const clearGoogleDriveAuthCache = () => {
  cachedAccessToken = null;
  cachedAccessTokenExpiresAt = null;
};

interface RequestAccessTokenOptions {
  forceAccountSelect?: boolean;
}

const requestAccessToken = async (
  config: GoogleDrivePickerConfig,
  { forceAccountSelect = false }: RequestAccessTokenOptions = {}
) => {
  if (forceAccountSelect) {
    clearGoogleDriveAuthCache();
  } else if (hasValidCachedAccessToken()) {
    return cachedAccessToken as string;
  }

  const tokenClient = await getTokenClient(config);

  return await new Promise<string>((resolve, reject) => {
    tokenClient.callback = (response) => {
      if (response?.error) {
        reject(
          new GoogleDrivePickerError(
            'auth_failed',
            response.error_description || response.error
          )
        );
        return;
      }

      if (!response?.access_token) {
        reject(
          new GoogleDrivePickerError(
            'auth_failed',
            'Google Drive authorization did not return an access token.'
          )
        );
        return;
      }

      cachedAccessToken = response.access_token;
      cachedAccessTokenExpiresAt = response.expires_in
        ? Date.now() +
          (response.expires_in - ACCESS_TOKEN_REFRESH_SAFETY_SECONDS) * 1000
        : null;
      resolve(response.access_token);
    };

    tokenClient.error_callback = (error) => {
      if (error?.type === 'popup_closed') {
        reject(
          new GoogleDrivePickerError(
            'auth_cancelled',
            'Google Drive authorization was cancelled.'
          )
        );
        return;
      }

      reject(
        new GoogleDrivePickerError(
          'auth_failed',
          error?.message ||
            error?.type ||
            'Google Drive authorization failed.'
        )
      );
    };

    try {
      let prompt: string;
      if (forceAccountSelect) {
        prompt = 'select_account';
      } else if (cachedAccessToken) {
        prompt = '';
      } else {
        prompt = 'consent';
      }

      tokenClient.requestAccessToken({ prompt });
    } catch (error) {
      reject(
        new GoogleDrivePickerError(
          'auth_failed',
          error instanceof Error
            ? error.message
            : 'Unable to request Google Drive authorization.'
        )
      );
    }
  });
};

const normalizeGooglePickerLocale = (locale?: string) =>
  locale?.split('-')[0] || undefined;

const getPickerDocumentValue = (
  document: GoogleDrivePickerDocument,
  pickerDocumentKey: string | undefined,
  fallbackKey: string
) => {
  const value =
    (pickerDocumentKey && document[pickerDocumentKey]) || document[fallbackKey];

  return typeof value === 'string' ? value : undefined;
};

const getPickerDocumentId = (document: GoogleDrivePickerDocument) =>
  getPickerDocumentValue(document, getGooglePickerApi()?.Document?.ID, 'id');

const getPickerDocumentName = (document: GoogleDrivePickerDocument) =>
  getPickerDocumentValue(
    document,
    getGooglePickerApi()?.Document?.NAME,
    'name'
  );

const getPickerDocumentMimeType = (document: GoogleDrivePickerDocument) =>
  getPickerDocumentValue(
    document,
    getGooglePickerApi()?.Document?.MIME_TYPE,
    'mimeType'
  );

const getPickerDocumentThumbnailUrl = (document: GoogleDrivePickerDocument) =>
  getPickerDocumentValue(
    document,
    getGooglePickerApi()?.Document?.THUMBNAIL_URL,
    'thumbnailUrl'
  );

const parseFileSize = (size: unknown) => {
  const parsedSize = Number(size);

  return Number.isFinite(parsedSize) ? parsedSize : undefined;
};

interface GoogleDriveMediaMetadata {
  width?: number;
  height?: number;
  durationMs?: number;
}

const fetchGoogleDriveMediaMetadata = async (
  fileId: string,
  accessToken: string
): Promise<GoogleDriveMediaMetadata> => {
  try {
    const url = `${GOOGLE_DRIVE_API_BASE_URL}/${encodeURIComponent(
      fileId
    )}?fields=imageMediaMetadata,videoMediaMetadata`;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    if (!response.ok) {
      console.warn(
        `Google Drive media metadata fetch failed: ${response.status}.`
      );
      return {};
    }
    const data = (await response.json()) as {
      imageMediaMetadata?: { width?: number; height?: number };
      videoMediaMetadata?: {
        width?: number;
        height?: number;
        durationMillis?: string;
      };
    };
    const durationMillis = data.videoMediaMetadata?.durationMillis;
    return {
      width:
        data.imageMediaMetadata?.width ?? data.videoMediaMetadata?.width,
      height:
        data.imageMediaMetadata?.height ?? data.videoMediaMetadata?.height,
      durationMs: durationMillis ? Number(durationMillis) : undefined
    };
  } catch (error) {
    console.warn('Google Drive media metadata fetch threw.', error);
    return {};
  }
};

export const mapGoogleDriveDocumentsToCloudFiles = async ({
  accessToken,
  allowedMimeTypes,
  documents
}: {
  accessToken: string;
  allowedMimeTypes: string[];
  documents: GoogleDrivePickerDocument[];
}): Promise<CloudPickerFile[]> => {
  const filteredDocuments = (documents || []).filter((document) => {
    if (!getPickerDocumentId(document)) {
      return false;
    }
    const mimeType = getPickerDocumentMimeType(document);
    return !mimeType || allowedMimeTypes.includes(mimeType);
  });

  const metadata = await Promise.all(
    filteredDocuments.map((document) =>
      fetchGoogleDriveMediaMetadata(
        getPickerDocumentId(document) as string,
        accessToken
      )
    )
  );

  return filteredDocuments.map((document, index) => {
    const fileId = getPickerDocumentId(document) as string;
    const dimensions = metadata[index];
    const mimeType = getPickerDocumentMimeType(document);
    return {
      provider: 'google_drive' as const,
      name: getPickerDocumentName(document),
      size: parseFileSize(document.sizeBytes),
      mimeType,
      width: dimensions.width,
      height: dimensions.height,
      durationMs: dimensions.durationMs,
      thumbnailUrl: getPickerDocumentThumbnailUrl(document),
      getUploadDescriptor: async () => ({
        url: buildGoogleDriveDownloadUrl(fileId),
        accessToken
      }),
      toBrowserFile: async (onProgress) =>
        await fetchBrowserFileFromUrl(
          buildGoogleDriveDownloadUrl(fileId),
          { headers: { Authorization: `Bearer ${accessToken}` } },
          {
            providedName: getPickerDocumentName(document),
            mimeType,
            fallbackBaseName: `google-drive-file-${Date.now()}-${index + 1}`
          },
          onProgress
        )
    };
  });
};

export const getGoogleDrivePickedFilesFromResponse = async (
  data: GoogleDrivePickerResponse,
  accessToken: string,
  allowedMimeTypes: string[]
): Promise<CloudPickerFile[] | null> => {
  const picker = getGooglePickerApi();
  const action =
    (picker?.Response?.ACTION && data[picker.Response.ACTION]) || data.action;
  const pickedAction = picker?.Action?.PICKED || 'picked';
  const cancelAction = picker?.Action?.CANCEL || 'cancel';

  if (action === pickedAction) {
    const documents =
      (picker?.Response?.DOCUMENTS && data[picker.Response.DOCUMENTS]) ||
      data.docs ||
      [];

    return await mapGoogleDriveDocumentsToCloudFiles({
      accessToken,
      allowedMimeTypes,
      documents: documents as GoogleDrivePickerDocument[]
    });
  }

  if (action === cancelAction) {
    return [];
  }

  return null;
};

const preloadGoogleDrivePicker = async (config: GoogleDrivePickerConfig) => {
  validateGoogleDriveConfig(config);
  await Promise.all([loadGooglePickerApi(), getTokenClient(config)]);
};

const launchGoogleDrivePicker = async (
  { allowVideoAndPdf, locale, forceAccountSelect }: LaunchCloudPickerOptions,
  config: GoogleDrivePickerConfig
): Promise<CloudPickerFile[]> => {
  await preloadGoogleDrivePicker(config);

  let accessToken: string;
  try {
    accessToken = await requestAccessToken(config, { forceAccountSelect });
  } catch (error) {
    if (
      isGoogleDrivePickerError(error) &&
      error.code === 'auth_cancelled'
    ) {
      return [];
    }
    throw error;
  }

  const pickerApi = window.google?.picker;

  if (!pickerApi) {
    throw new GoogleDrivePickerError(
      'auth_failed',
      'Google Picker API is unavailable.'
    );
  }

  const allowedMimeTypes = getAllowedGoogleDriveMimeTypes(allowVideoAndPdf);
  const mimeTypeList = allowedMimeTypes.join(',');

  return await new Promise<CloudPickerFile[]>((resolve, reject) => {
    try {
      const docsView = new pickerApi.DocsView(pickerApi.ViewId.DOCS)
        .setIncludeFolders(true)
        .setSelectFolderEnabled(false)
        .setMimeTypes(mimeTypeList);

      let settled = false;
      const finish = (files: CloudPickerFile[]) => {
        if (!settled) {
          settled = true;
          resolve(files);
        }
      };

      const pickerBuilder = new pickerApi.PickerBuilder()
        .addView(docsView)
        .setOrigin(window.location.origin)
        .setOAuthToken(accessToken)
        .setDeveloperKey(config.apiKey)
        .setAppId(config.appId)
        .setCallback(async (data: GoogleDrivePickerResponse) => {
          try {
            const files = await getGoogleDrivePickedFilesFromResponse(
              data,
              accessToken,
              allowedMimeTypes
            );

            if (files !== null) {
              finish(files);
            }
          } catch (error) {
            reject(
              new GoogleDrivePickerError(
                'download_failed',
                error instanceof Error
                  ? error.message
                  : 'Unable to process the Google Drive selection.'
              )
            );
          }
        })
        .setMaxItems(MAX_MEDIA_FILES)
        .setSelectableMimeTypes(mimeTypeList)
        .enableFeature(pickerApi.Feature.MULTISELECT_ENABLED)
        .enableFeature(pickerApi.Feature.SUPPORT_DRIVES);

      const normalizedLocale = normalizeGooglePickerLocale(locale);
      if (normalizedLocale) {
        pickerBuilder.setLocale(normalizedLocale);
      }

      const picker = pickerBuilder.build();
      picker.setVisible(true);
    } catch (error) {
      reject(
        new GoogleDrivePickerError(
          'auth_failed',
          error instanceof Error
            ? error.message
            : 'Unable to open the Google Drive picker.'
        )
      );
    }
  });
};

export const createGoogleDrivePicker = (
  config: GoogleDrivePickerConfig
): CloudPickerAdapter => ({
  preload: async () => await preloadGoogleDrivePicker(config),
  launch: async (options) => await launchGoogleDrivePicker(options, config),
  getErrorTranslationKey: (error) => {
    if (isGoogleDrivePickerError(error)) {
      switch (error.code) {
        case 'config_missing':
          return 'lomavisCreator.additionalOptions.googleDriveConfigMissing';
        case 'auth_failed':
          return 'lomavisCreator.additionalOptions.googleDriveAuthFailed';
        case 'download_failed':
          return 'lomavisCreator.additionalOptions.googleDriveDownloadFailed';
        default:
          return 'lomavisCreator.additionalOptions.cloudImportFailed';
      }
    }

    return 'lomavisCreator.additionalOptions.cloudImportFailed';
  }
});
